<template>
  <div class="join-page">

    <!-- Uygulamayla aynı üst çubuk: davetli bu sayfayı ilk kez görüyor -->
    <header class="topbar">
      <a class="brand" href="../">
        <img src="../assets/logo2.png" alt="logo" @error="logoFailed = true" v-if="!logoFailed">
        <span v-else>ESignature Platform</span>
      </a>
      <LanguageSwitcher />
    </header>

  <section class="section">
    <div class="container" style="max-width: 560px;">

      <div class="box has-text-centered" v-if="loading">
        <p class="has-text-grey">{{ $t('join.loading') }}</p>
      </div>

      <!-- Davet geçersiz -->
      <div class="box has-text-centered" v-else-if="blockedMessage">
        <span class="icon is-large has-text-grey"><i class="fa fa-lock fa-2x"></i></span>
        <p class="title is-5 mt-3">{{ blockedMessage }}</p>
        <p class="has-text-grey">{{ $t('join.blockedHint') }}</p>
      </div>

      <!-- Katıldı -->
      <div class="box has-text-centered" v-else-if="joined">
        <span class="icon is-large has-text-success"><i class="fa fa-check-circle fa-2x"></i></span>
        <p class="title is-5 mt-3">{{ joinedMessage }}</p>
        <p class="has-text-grey">{{ $t('join.joinedHint') }}</p>
        <div class="buttons is-centered mt-4">
          <a class="button is-primary" href="../">{{ $t('join.goToApp') }}</a>
        </div>
      </div>

      <!-- Davet -->
      <div class="box has-text-centered" v-else>
        <p class="has-text-grey is-size-7">{{ invitation.organizationName }}</p>
        <h1 class="title is-5 mt-2">{{ $t('join.title') }}</h1>
        <p class="has-text-grey mb-4">
          {{ $t('join.lead', { org: invitation.organizationName }) }}
        </p>

        <div class="notification is-light is-size-7">
          <p><strong>{{ invitation.name }}</strong></p>
          <p class="has-text-grey">{{ invitation.email }}</p>
        </div>

        <div class="notification is-danger" v-if="errorMessage">
          <button class="delete" @click="errorMessage = ''"></button>
          {{ errorMessage }}
        </div>

        <!-- Giriş yapılmamış -->
        <template v-if="!signedIn">
          <p class="has-text-grey is-size-7 mb-3">{{ $t('join.signInFirst') }}</p>
          <button class="button is-primary is-fullwidth" :class="{ 'is-loading': signingIn }"
                  @click="signIn">
            <span class="icon"><i class="fa fa-sign-in"></i></span>
            <span>{{ signingIn ? $t('join.signingIn') : $t('join.signIn') }}</span>
          </button>
        </template>

        <!-- Giriş yapılmış -->
        <template v-else>
          <button class="button is-primary is-fullwidth" :class="{ 'is-loading': saving }"
                  @click="accept">
            {{ $t('join.accept') }}
          </button>
          <p class="help mt-3">{{ $t('join.oneOrgOnly') }}</p>
        </template>
      </div>
    </div>
  </section>
  </div>
</template>

<script>
import api from '../config/api';
import LanguageSwitcher from './LanguageSwitcher.vue';

export default {
  name: 'Join',
  components: { LanguageSwitcher },
  props: {
    inviteToken: { type: String, default: '' }
  },
  data() {
    return {
      token: this.inviteToken
        || (window.location.hash.match(/#\/(?:davet|join)\/([A-Za-z0-9]+)/) || [])[1]
        || '',
      loading: true,
      saving: false,
      blockedMessage: '',
      errorMessage: '',
      joined: false,
      joinedMessage: '',
      invitation: {},
      signingIn: false,
      logoFailed: false
    };
  },
  watch: {
    // Giriş tamamlandığı anda kullanıcıyı bir kez daha butona bastırmıyoruz;
    // zaten daveti kabul etmek için gelmişti.
    signedIn(isIn) {
      if (isIn && this.signingIn && !this.joined) {
        this.signingIn = false;
        this.accept();
      }
    }
  },
  computed: {
    signedIn() {
      const app = this.$store ? this.$store.state.the_app_data : null;
      return !!(app && app.token);
    }
  },
  mounted() {
    if (!this.token) {
      this.blockedMessage = this.$t('join.missingToken');
      this.loading = false;
      return;
    }
    this.load();
  },
  methods: {
    load() {
      api.get('/invitations/' + this.token)
        .then((body) => {
          this.invitation = body.invitation;
          // Davet hangi dilde gönderildiyse arayüz de o dile geçiyor.
          // Davetlinin tarayıcı dili farklı olabilir; ona anlamlı olan,
          // e-postada gördüğü dil.
          if (body.invitation.locale) this.$setLocale(body.invitation.locale);
        })
        .catch((err) => { this.blockedMessage = err.message; })
        .then(() => { this.loading = false; });
    },

    signIn() {
      if (!this.$store) return;
      this.signingIn = true;
      this.errorMessage = '';
      // Giriş sonrası kullanıcı aynı sayfaya döner; hash adreste kaldığı
      // için token kaybolmuyor
      this.$store.dispatch('login')
        .catch((message) => {
          this.signingIn = false;
          this.errorMessage = String(message);
        });
    },

    accept() {
      if (this.saving) return;
      this.saving = true;
      this.errorMessage = '';

      api.post('/invitations/' + this.token + '/accept')
        .then((body) => {
          this.joined = true;
          this.joinedMessage = body.message;
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
.join-page {
  min-height: 100vh;
  background: #f4f6fb;
}

/* Sign sayfasıyla aynı çubuk: davetli ikisini de görebiliyor */
.topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 22px;
  background: #1c1c33;
  backdrop-filter: blur(8px);
  border-bottom: 1px solid #e6e8f0;
}

.brand {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: #22223d;
  letter-spacing: -0.01em;
}

.brand img {
  display: block;
  max-height: 30px;
}
</style>


// WEBPACK FOOTER //
// src/Components/Join.vue